import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  adjustBalance,
  assignAbsenceTypeToEmployee,
  cancelImportedAbsence,
  deleteAbsenceRequest,
  getAbsenceTypes,
  getEmployeeAbsences,
  getEmployeeAssignedAbsenceTypes,
  unassignEmployeeFromAbsenceType,
  type AbsenceType,
  type EmployeeAbsence,
  type EmployeeAssignedAbsenceType,
} from '@/lib/absences';
import { CreateAbsenceRequestDialog } from '@/features/absences/components/create-absence-request-dialog';
import { formatDate, formatDateRange } from '@/lib/date';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import type { EmployeeDetail } from '@/types/employee';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Ban,
  Calculator,
  CalendarDays,
  Check,
  History,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { AccrualExplanationDialog } from './accrual-explanation-dialog';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';

interface AbsencesTabProps {
  employee: EmployeeDetail;
}

// Query keys
const absenceKeys = {
  employeePolicies: (employeeId: string) => ['employee-policies', employeeId] as const,
  employeeAbsences: (employeeId: string, year?: number) =>
    ['employee-absences', employeeId, year] as const,
};

/**
 * Whether the viewer may edit/delete a local absence record, mirroring backend
 * authorization: APPROVED or past records need `edit-past`, future PENDING
 * records need `manage`. Imported and finalised (cancelled/recalled/rejected)
 * rows are not editable.
 */
function canEditLocalRecord(
  a: Pick<EmployeeAbsence, 'source' | 'status' | 'startDate'>,
  todayYmd: string,
  perms: { canEditPast: boolean; canManageRequests: boolean }
): boolean {
  if (a.source !== 'local' || (a.status !== 'approved' && a.status !== 'pending')) return false;
  const pastOrApproved = a.status === 'approved' || a.startDate < todayYmd;
  return pastOrApproved ? perms.canEditPast : perms.canManageRequests;
}

// Loading skeleton
function AbsencesTabSkeleton() {
  return (
    <div className="space-y-5">
      {/* Policies skeleton */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-7 w-24" />
        </div>
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      </div>

      {/* History skeleton */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="p-5 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// YEAR STRIP — visual annual timeline
// ============================================================================

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

function dayOfYear(d: Date): number {
  // 1-based day-of-year. Jan 1 = 1.
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  const here = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.floor((here - start) / 86_400_000) + 1;
}

function formatLedgerDate(startIso: string, endIso: string): string {
  return formatDateRange(startIso, endIso, 'short');
}

interface StripEvent {
  type: 'absence' | 'accrual';
  /** ISO date YYYY-MM-DD for start (used for absolute-position math). */
  startDate: string;
  /** ISO date YYYY-MM-DD for end, inclusive. Same as startDate for accruals. */
  endDate: string;
  color: string;
  label: string;
  amount: number;
}

/**
 * Convert an ISO YYYY-MM-DD date to a fractional position [0..1] within the
 * span defined by `years`. Each year occupies 1/years.length of the strip.
 * Returns null when the date falls outside the rendered span.
 */
function positionInYears(iso: string, years: number[]): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  const y = d.getUTCFullYear();
  const idx = years.indexOf(y);
  if (idx < 0) return null;
  const frac = (dayOfYear(d) - 1) / daysInYear(y);
  return (idx + frac) / years.length;
}

function YearStrip({
  years,
  events,
  today,
}: {
  /** The years to render side-by-side, oldest first. e.g. [2025, 2026]. */
  years: number[];
  events: StripEvent[];
  /** Current date — draws a vertical "today" line if it falls inside `years`. */
  today: Date | null;
}) {
  if (years.length === 0) return null;

  // Per-year metadata for gridlines + month labels.
  const yearSegments = years.map((y, idx) => {
    const startFrac = idx / years.length;
    const widthFrac = 1 / years.length;
    const monthStarts: number[] = [];
    for (let m = 0; m < 12; m++) {
      monthStarts.push(
        ((dayOfYear(new Date(Date.UTC(y, m, 1))) - 1) / daysInYear(y)) * widthFrac
      );
    }
    return { year: y, startFrac, widthFrac, monthStarts };
  });

  const todayPos =
    today != null ? positionInYears(today.toISOString().slice(0, 10), years) : null;

  const absences = events.filter((e) => e.type === 'absence');
  const accruals = events.filter((e) => e.type === 'accrual');
  const compact = years.length > 1;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card/50">
      {/* Header */}
      <div className="flex items-baseline justify-between px-4 pt-3 pb-2">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/80">
          {years.length === 1
            ? `${years[0]} timeline`
            : `${years[0]} – ${years[years.length - 1]} timeline`}
        </p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/70 font-mono">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-px bg-foreground/50" />
            today
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-hygge" />
            accrual
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm bg-foreground/40" />
            days off
          </span>
        </div>
      </div>

      {/* The strip itself */}
      <div className="relative h-[64px] mx-4 mb-1">
        {/* Month gridlines (per year) */}
        {yearSegments.flatMap((seg) =>
          seg.monthStarts.map((pos, i) => (
            <div
              key={`grid-${seg.year}-${i}`}
              className="absolute top-0 bottom-7 w-px bg-border/30"
              style={{ left: `${(seg.startFrac + pos) * 100}%` }}
            />
          ))
        )}

        {/* Year boundary dividers — stronger lines between years */}
        {yearSegments.slice(1).map((seg) => (
          <div
            key={`boundary-${seg.year}`}
            className="absolute top-0 bottom-0 w-px bg-foreground/15"
            style={{ left: `${seg.startFrac * 100}%` }}
          />
        ))}

        {/* Today line */}
        {todayPos != null && (
          <div
            className="absolute top-0 bottom-7 w-px bg-foreground/60"
            style={{ left: `${todayPos * 100}%` }}
            title="Today"
          />
        )}

        {/* Absence blocks */}
        {absences.map((e, i) => {
          const startPos = positionInYears(e.startDate, years);
          const endPos = positionInYears(e.endDate, years);
          if (startPos == null || endPos == null) return null;
          // For multi-day absences, width spans from start to end + 1 day's-worth
          const dayWidth = 1 / (years.length * 365);
          const width = Math.max(0.0035, endPos - startPos + dayWidth);
          return (
            <TooltipProvider key={`a-${i}`} delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="absolute top-[10px] h-[18px] rounded-sm cursor-help"
                    style={{
                      left: `${startPos * 100}%`,
                      width: `${width * 100}%`,
                      backgroundColor: e.color,
                      opacity: 0.9,
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent className="text-[11px]">
                  {e.label} · {e.amount}d
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}

        {/* Accrual markers */}
        {accruals.map((e, i) => {
          const pos = positionInYears(e.startDate, years);
          if (pos == null) return null;
          return (
            <TooltipProvider key={`m-${i}`} delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="absolute top-[2px] h-[30px] w-px cursor-help"
                    style={{ left: `${pos * 100}%`, backgroundColor: e.color }}
                  >
                    <div
                      className="absolute -top-0.5 -left-[3px] h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: e.color }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-[11px]">
                  {e.label}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}

        {/* Year labels — bottom row, prominent */}
        {yearSegments.map((seg) => (
          <span
            key={`year-${seg.year}`}
            className="absolute bottom-3.5 font-mono text-[10px] font-semibold tracking-wider text-foreground/70"
            style={{ left: `calc(${seg.startFrac * 100}% + 4px)` }}
          >
            {seg.year}
          </span>
        ))}

        {/* Month labels — bottom row, abbreviated. With 2 years (24 labels)
            we drop to 1-letter, otherwise full 3-letter. */}
        {yearSegments.flatMap((seg) =>
          seg.monthStarts.map((pos, i) => (
            <span
              key={`lbl-${seg.year}-${i}`}
              className="absolute bottom-0 font-mono text-[8.5px] uppercase tracking-wider text-muted-foreground/55"
              style={{ left: `calc(${(seg.startFrac + pos) * 100}% + 3px)` }}
            >
              {compact ? MONTH_LABELS[i].charAt(0) : MONTH_LABELS[i]}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// BALANCE HERO — prominent per-type balance with progress + action popover
// ============================================================================

function BalanceHero({
  item,
  canManage,
  onAdjust,
  onUnassign,
  isUnassigning,
  employeeId,
}: {
  item: EmployeeAssignedAbsenceType;
  canManage: boolean;
  onAdjust: () => void;
  onUnassign: () => void;
  isUnassigning: boolean;
  employeeId: string;
}) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const at = item.absenceType;
  const isUnlimited = at.unlimitedBalance;
  const available = item.balance.available;
  const accrued = item.balance.accrued;
  const used = item.balance.used ?? 0;
  const pending = item.balance.pending ?? 0;
  const color = at.color || '#9CA3AF';

  // Progress bar — share of accrued already used+pending. For unlimited, no bar.
  const consumed = used + pending;
  const total = accrued ?? 0;
  const consumedPct = !isUnlimited && total > 0 ? Math.min(100, Math.round((consumed / total) * 100)) : 0;

  return (
    <>
      <div
        className={cn(
          'group relative overflow-hidden rounded-xl border border-border bg-card p-5',
          isUnassigning && 'opacity-50',
          item.isInactive && 'border-dashed opacity-70'
        )}
      >
        {/* Header: type name + actions menu */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground truncate">
              {at.name}
            </h3>
            {item.isInactive && (
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 px-1.5 py-0.5 rounded bg-muted">
                Inactive
              </span>
            )}
          </div>
          <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Actions"
                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 -mr-1 -mt-1 rounded hover:bg-muted flex items-center justify-center cursor-pointer"
              >
                <span className="text-muted-foreground text-base leading-none">⋯</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1.5">
              <button
                type="button"
                onClick={() => {
                  setActionsOpen(false);
                  setExplanationOpen(true);
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted text-[12px] cursor-pointer"
              >
                <Calculator className="h-3.5 w-3.5 text-muted-foreground" />
                View calculation
              </button>
              {canManage && !item.isInactive && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      onAdjust();
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted text-[12px] cursor-pointer"
                  >
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    Adjust balance
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      onUnassign();
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-destructive/10 text-[12px] text-destructive cursor-pointer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Unassign type
                  </button>
                </>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Hero number */}
        <div className="flex items-baseline gap-2.5">
          {isUnlimited ? (
            <>
              <span className="text-[40px] font-semibold leading-none italic tracking-tight text-foreground">
                Unlimited
              </span>
            </>
          ) : (
            <>
              <span className="text-[44px] font-semibold leading-none tabular-nums text-foreground">
                {available != null ? available.toFixed(available % 1 === 0 ? 0 : 1) : '0'}
              </span>
              <span className="text-[13px] text-muted-foreground">days available</span>
            </>
          )}
        </div>

        {/* Sub-stats row — used / accrued / pending */}
        {!isUnlimited && (
          <>
            <div className="mt-3 flex items-baseline gap-4 text-[12px] text-muted-foreground">
              <span>
                <span className="font-mono tabular-nums text-foreground">{used.toFixed(used % 1 === 0 ? 0 : 1)}</span>{' '}
                used
              </span>
              <span>
                <span className="font-mono tabular-nums text-foreground">{(accrued ?? 0).toFixed((accrued ?? 0) % 1 === 0 ? 0 : 1)}</span>{' '}
                earned
              </span>
              {pending > 0 && (
                <span>
                  <span className="font-mono tabular-nums text-foreground">{pending.toFixed(pending % 1 === 0 ? 0 : 1)}</span>{' '}
                  pending
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${consumedPct}%`, backgroundColor: color }}
              />
            </div>
          </>
        )}

        {/* Next accrual */}
        {item.accrual?.nextDate && (
          <p className="mt-3 text-[12px] text-muted-foreground">
            Next{' '}
            <span className="font-semibold text-foreground">
              +{item.accrual.nextAmount} day{item.accrual.nextAmount === 1 ? '' : 's'}
            </span>{' '}
            on{' '}
            <span className="font-mono tabular-nums text-foreground">
              {formatDate(item.accrual.nextDate, 'short')}
            </span>
          </p>
        )}
        {isUnlimited && (
          <p className="mt-3 text-[12px] text-muted-foreground italic">
            No fixed accrual schedule · {used.toFixed(used % 1 === 0 ? 0 : 1)} day{used === 1 ? '' : 's'} taken this year
          </p>
        )}
      </div>
      <AccrualExplanationDialog
        open={explanationOpen}
        onOpenChange={setExplanationOpen}
        employeeId={employeeId}
        absenceTypeId={at.id}
        absenceTypeName={at.name}
        absenceTypeColor={at.color || '#9CA3AF'}
      />
    </>
  );
}

// ============================================================================
// LEDGER — chronological feed of events for the year
// ============================================================================

interface LedgerItem {
  key: string;
  direction: 'up' | 'down';
  date: string; // ISO YYYY-MM-DD or range like "Mar 25 – 26"
  dateRaw: string; // for sorting (ISO YYYY-MM-DD of start)
  isFuture: boolean;
  primary: React.ReactNode;
  secondary?: string;
  pill: { label: string; tone: 'approved' | 'pending' | 'upcoming' | 'pf' | 'rejected' };
  /** Imported PeopleForce record id, set only when the row is live and the
   *  viewer may void it. Drives the cancel affordance. */
  recordId?: string;
  /** Human-readable summary shown in the cancel confirmation dialog. */
  cancelLabel?: string;
  /** Local AbsenceRequest id — present only on editable local rows. */
  editRequestId?: string;
  /** Human label for the delete confirmation, e.g. "−5 days Vacation (Mar 2)". */
  editLabel?: string;
}

const PILL_TONES: Record<LedgerItem['pill']['tone'], string> = {
  approved: 'bg-emerald-500/10 text-emerald-400',
  pending: 'bg-amber-500/10 text-amber-400',
  upcoming: 'bg-hygge/10 text-hygge',
  pf: 'bg-muted text-muted-foreground',
  rejected: 'bg-red-500/10 text-red-400',
};

function LedgerEventRow({
  item,
  onCancel,
  onEdit,
  onDelete,
}: {
  item: LedgerItem;
  /** Present only when the viewer may void imported records. Row renders the
   *  cancel button when this is set AND the item carries a `recordId`.
   *  For local records, use `onEdit`/`onDelete` instead. */
  onCancel?: (recordId: string, label: string) => void;
  onEdit?: (requestId: string) => void;
  onDelete?: (requestId: string, label: string) => void;
}) {
  const canCancel = !!item.recordId && !!onCancel;
  const canEditDelete = !!item.editRequestId && !!onEdit && !!onDelete;
  return (
    <div
      className={cn(
        'grid grid-cols-[1.25rem_11rem_1fr_auto] gap-3 items-baseline px-4 py-2.5 text-[13px]',
        item.isFuture && 'opacity-60'
      )}
    >
      {item.direction === 'up' ? (
        <ArrowUp
          className={cn(
            'h-3.5 w-3.5 self-center',
            item.isFuture ? 'text-hygge/70' : 'text-emerald-500'
          )}
        />
      ) : (
        <ArrowDown className="h-3.5 w-3.5 text-muted-foreground self-center" />
      )}
      <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
        {item.date}
      </span>
      <div className="min-w-0">
        <div className="text-foreground">{item.primary}</div>
        {item.secondary && (
          <div className="text-[11px] text-muted-foreground/80 mt-0.5">{item.secondary}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0 self-center">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
            PILL_TONES[item.pill.tone]
          )}
        >
          {item.pill.label}
        </span>
        {canCancel && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground/60 hover:text-red-400 cursor-pointer"
                  onClick={() =>
                    onCancel!(item.recordId!, item.cancelLabel ?? 'this imported record')
                  }
                >
                  <Ban className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cancel imported record &amp; restore balance</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {canEditDelete && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground/60 hover:text-foreground cursor-pointer"
                  onClick={() => onEdit!(item.editRequestId!)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit absence record</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground/60 hover:text-red-400 cursor-pointer"
                  onClick={() => onDelete!(item.editRequestId!, item.editLabel ?? 'this absence record')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete absence record &amp; adjust balance</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

// Main component
export function AbsencesTab({ employee }: AbsencesTabProps) {
  const currentYear = new Date().getFullYear();
  const [addPolicyDialogOpen, setAddPolicyDialogOpen] = useState(false);
  const [adjustingType, setAdjustingType] = useState<EmployeeAssignedAbsenceType | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Check if current user can manage policies (HR Admin or Super Admin)
  const canManagePolicies = user?.role === 'super_admin' || user?.role === 'hr_admin';

  // Cancelling an imported PeopleForce record is gated on the exact permission
  // the backend enforces (`absences.requests.manage`), NOT on role — so the UI
  // matches server authorization and any custom role granted that permission
  // gets the action. super_admin passes via hasPermission's built-in bypass.
  const canManageRequests = hasPermission(user, 'absences.requests.manage');
  const canCancelImported = canManageRequests;

  // Editing/deleting processed local records is gated on the dedicated permission.
  const canEditPast = hasPermission(user, 'absences.requests.edit-past');

  // Fetch assigned absence types
  const {
    data: assignedTypesData,
    isLoading: assignedTypesLoading,
    error: assignedTypesError,
  } = useQuery({
    queryKey: absenceKeys.employeePolicies(employee.id),
    queryFn: () => getEmployeeAssignedAbsenceTypes(employee.id),
  });

  // Fetch all available absence types (only when dialog is open)
  const { data: allAbsenceTypes } = useQuery({
    queryKey: ['absence-types'],
    queryFn: getAbsenceTypes,
    enabled: addPolicyDialogOpen,
  });

  // Fetch unified absence history (local AbsenceRequest + PeopleForce-imported
  // ImportedAbsenceRecord, merged on the backend, sorted by startDate desc).
  // No year scope — the tab shows one continuous history; events span as many
  // years as we have records for.
  const {
    data: absences,
    isLoading: absencesLoading,
    error: absencesError,
  } = useQuery({
    queryKey: absenceKeys.employeeAbsences(employee.id),
    queryFn: () => getEmployeeAbsences(employee.id),
  });

  // Strip window — rolling 2-year window [currentYear-1, currentYear], clamped
  // to the PeopleForce migration cutoff (2025). Used purely for which years
  // the strip RENDERS; events are filtered from the same `absences` query.
  const STRIP_HISTORY_START = 2025;
  const stripYears: number[] = [];
  if (currentYear - 1 >= STRIP_HISTORY_START) stripYears.push(currentYear - 1);
  stripYears.push(currentYear);

  // Mutation to unassign policy from employee
  const unassignMutation = useMutation({
    mutationFn: (absenceTypeId: string) =>
      unassignEmployeeFromAbsenceType(absenceTypeId, employee.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeePolicies(employee.id) });
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
    },
  });

  // Mutation to assign absence type to employee
  const assignMutation = useMutation({
    mutationFn: ({ absenceTypeId, effectiveFrom }: { absenceTypeId: string; effectiveFrom?: string }) =>
      assignAbsenceTypeToEmployee(absenceTypeId, employee.id, effectiveFrom),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeePolicies(employee.id) });
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      setAddPolicyDialogOpen(false);
    },
  });

  // Cancel an imported PeopleForce record — voids it and restores the balance.
  // `cancelTarget` drives the confirmation dialog; null when closed.
  const [cancelTarget, setCancelTarget] = useState<{ recordId: string; label: string } | null>(
    null
  );
  const cancelImportedMutation = useMutation({
    mutationFn: (recordId: string) => cancelImportedAbsence(recordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeeAbsences(employee.id) });
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeePolicies(employee.id) });
      setCancelTarget(null);
    },
  });

  // Edit/delete of local records.
  const [editRequestId, setEditRequestId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ requestId: string; label: string } | null>(
    null
  );
  const deleteRequestMutation = useMutation({
    mutationFn: (requestId: string) => deleteAbsenceRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeeAbsences(employee.id) });
      queryClient.invalidateQueries({ queryKey: absenceKeys.employeePolicies(employee.id) });
      // The New/Edit Request dialog reads balances under its own key — refresh it
      // too, or the type cards keep showing the pre-delete (stale) balance.
      queryClient.invalidateQueries({
        queryKey: ['employee-assigned-absence-types', employee.id],
      });
      setDeleteTarget(null);
    },
  });

  const isLoading = assignedTypesLoading || absencesLoading;
  const hasError = assignedTypesError || absencesError;

  // Get absence types not yet assigned to this employee
  const assignedTypeIds = assignedTypesData?.data?.map((p) => p.absenceType.id) || [];
  const availableAbsenceTypes =
    allAbsenceTypes?.filter((t) => t.isActive && !assignedTypeIds.includes(t.id)) || [];

  if (isLoading) {
    return <AbsencesTabSkeleton />;
  }

  if (hasError) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card p-8">
        <div className="text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-destructive/60" />
          <h4 className="mt-4 font-medium text-foreground">Failed to load absence data</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            {(assignedTypesError as Error)?.message || (absencesError as Error)?.message}
          </p>
        </div>
      </div>
    );
  }

  const assignedTypes = assignedTypesData?.data || [];
  const absenceList = absences || [];

  const stripYearSet = new Set(stripYears);
  const today = new Date();
  // Local YYYY-MM-DD for comparing against record start dates (matches the
  // backend's local-midnight "is past" boundary used for edit/delete auth).
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Build strip events — only those within the rolling 2-year window.
  const stripEvents: StripEvent[] = [];
  for (const a of absenceList) {
    if (a.status === 'rejected' || a.status === 'cancelled') continue;
    const startYear = parseInt(a.startDate.slice(0, 4), 10);
    const endYear = parseInt(a.endDate.slice(0, 4), 10);
    if (!stripYearSet.has(startYear) && !stripYearSet.has(endYear)) continue;
    stripEvents.push({
      type: 'absence',
      startDate: a.startDate,
      endDate: a.endDate,
      color: a.absenceType?.color ?? '#9CA3AF',
      label: a.absenceType?.name ?? a.pfLeaveTypeName ?? 'Unmapped',
      amount: a.days,
    });
  }
  for (const t of assignedTypes) {
    if (!t.accrual?.nextDate) continue;
    const nextYear = parseInt(t.accrual.nextDate.slice(0, 4), 10);
    if (!stripYearSet.has(nextYear)) continue;
    stripEvents.push({
      type: 'accrual',
      startDate: t.accrual.nextDate,
      endDate: t.accrual.nextDate,
      color: t.absenceType.color || 'hsl(186 80% 48%)',
      label: `+${t.accrual.nextAmount} ${t.absenceType.name} on ${t.accrual.nextDate}`,
      amount: t.accrual.nextAmount,
    });
  }

  // ── Build ledger items — one continuous feed across all years ──────────────
  const ledger: LedgerItem[] = [];
  // Upcoming accrual events from the per-type schedule (one per type with a
  // nextDate that's still in the future).
  for (const t of assignedTypes) {
    if (!t.accrual?.nextDate) continue;
    const next = new Date(`${t.accrual.nextDate}T00:00:00Z`);
    const isFuture = next > today;
    if (!isFuture) continue;
    ledger.push({
      key: `accrual-${t.absenceType.id}-${t.accrual.nextDate}`,
      direction: 'up',
      date: formatLedgerDate(t.accrual.nextDate, t.accrual.nextDate),
      dateRaw: t.accrual.nextDate,
      isFuture: true,
      primary: (
        <>
          <span className="font-semibold text-hygge">+{t.accrual.nextAmount} days</span>{' '}
          {t.absenceType.name} accrual
        </>
      ),
      secondary: 'scheduled by policy',
      pill: { label: 'Upcoming', tone: 'upcoming' },
    });
  }
  // Past accrual events — cycles already credited since import/hire, so the
  // history shows when days were actually earned (not just upcoming ones).
  for (const t of assignedTypes) {
    for (const pa of t.accrual?.pastAccruals ?? []) {
      ledger.push({
        key: `accrual-past-${t.absenceType.id}-${pa.date}`,
        direction: 'up',
        date: formatLedgerDate(pa.date, pa.date),
        dateRaw: pa.date,
        isFuture: false,
        primary: (
          <>
            <span className="font-semibold text-hygge">+{pa.amount} days</span>{' '}
            {t.absenceType.name} accrual
          </>
        ),
        secondary: 'credited by policy',
        pill: { label: 'Credited', tone: 'approved' },
      });
    }
  }
  // Absences as ↓ events
  for (const a of absenceList) {
    const startStr = formatLedgerDate(a.startDate, a.endDate);
    // A record that no longer counts against the balance: a voided PF import, or
    // a local request that was cancelled/recalled (days returned) or rejected
    // (never taken). Struck through so the "−N days" doesn't read as still owed.
    const isVoided =
      a.source === 'peopleforce'
        ? !!a.cancelledAt
        : a.status === 'cancelled' || a.status === 'recalled' || a.status === 'rejected';
    const daysReturned =
      isVoided && (a.source === 'peopleforce' || a.status === 'cancelled' || a.status === 'recalled');
    const typeLabel = a.absenceType?.name ?? a.pfLeaveTypeName ?? 'Unmapped';
    ledger.push({
      key: `absence-${a.id}`,
      direction: 'down',
      date: startStr,
      dateRaw: a.startDate,
      isFuture: false,
      // Live imported records are cancellable by HR; drop the handle once voided
      // or for local requests so the row shows no cancel affordance.
      recordId:
        a.source === 'peopleforce' && !a.cancelledAt && canCancelImported ? a.id : undefined,
      cancelLabel: `−${a.days} ${a.days === 1 ? 'day' : 'days'} ${typeLabel} (${startStr})`,
      editRequestId: canEditLocalRecord(a, todayYmd, { canEditPast, canManageRequests })
        ? a.id
        : undefined,
      editLabel: `−${a.days} ${a.days === 1 ? 'day' : 'days'} ${typeLabel} (${startStr})`,
      primary: (
        <>
          <span
            className={cn('font-semibold', isVoided && 'line-through text-muted-foreground')}
          >
            −{a.days} {a.days === 1 ? 'day' : 'days'}
          </span>{' '}
          <span className={cn(isVoided && 'line-through text-muted-foreground')}>{typeLabel}</span>
          {a.notes && <span className="text-muted-foreground"> · {a.notes}</span>}
        </>
      ),
      secondary:
        a.source === 'peopleforce'
          ? isVoided
            ? 'imported from PeopleForce · cancelled, days returned'
            : 'imported from PeopleForce'
          : daysReturned
            ? 'local request · days returned'
            : 'local request',
      // PF voided records always read as "Cancelled"; local records keep their
      // own status label (Cancelled / Recalled / Rejected) — all struck through.
      pill: isVoided && a.source === 'peopleforce'
        ? { label: 'Cancelled', tone: 'pf' }
        : {
            label:
              a.status === 'approved'
                ? 'Approved'
                : a.status === 'pending'
                  ? 'Pending'
                  : a.status === 'rejected'
                    ? 'Rejected'
                    : a.status === 'recalled'
                      ? 'Recalled'
                      : 'Cancelled',
            tone:
              a.status === 'approved'
                ? 'approved'
                : a.status === 'pending'
                  ? 'pending'
                  : a.status === 'rejected'
                    ? 'rejected'
                    : 'pf',
          },
    });
  }
  // Sort: future first (ascending), then past (descending by date)
  ledger.sort((a, b) => {
    if (a.isFuture && !b.isFuture) return -1;
    if (!a.isFuture && b.isFuture) return 1;
    if (a.isFuture && b.isFuture) return a.dateRaw.localeCompare(b.dateRaw);
    return b.dateRaw.localeCompare(a.dateRaw);
  });

  return (
    <div className="space-y-5">
      {/* Header: year selector + add button */}
      <div className="flex items-baseline justify-between">
        <h2 className="text-[18px] font-semibold tracking-tight">
          Time off
        </h2>
        {canManagePolicies && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddPolicyDialogOpen(true)}
            className="h-8 gap-1.5 text-xs cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            Assign type
          </Button>
        )}
      </div>

      {/* Empty state — no assigned types at all */}
      {assignedTypes.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
            <CalendarDays className="h-6 w-6 text-muted-foreground/60" />
          </div>
          <h4 className="mt-4 text-sm font-medium text-foreground">No absence types assigned</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            This employee doesn't have any absence types assigned yet.
          </p>
        </div>
      ) : (
        <>
          {/* Balance heroes — primary "how many days do I have" answer, one per type */}
          <div
            className={cn(
              'grid gap-3',
              assignedTypes.length === 1
                ? 'grid-cols-1'
                : assignedTypes.length === 2
                  ? 'sm:grid-cols-2'
                  : 'sm:grid-cols-2 lg:grid-cols-3'
            )}
          >
            {assignedTypes.filter((t) => !t.isInactive).map((item) => (
              <BalanceHero
                key={item.assignmentId ?? item.absenceType.id}
                item={item}
                canManage={canManagePolicies}
                employeeId={employee.id}
                onUnassign={() => unassignMutation.mutate(item.absenceType.id)}
                isUnassigning={
                  unassignMutation.isPending &&
                  unassignMutation.variables === item.absenceType.id
                }
                onAdjust={() => setAdjustingType(item)}
              />
            ))}
            {assignedTypes.filter((t) => t.isInactive).map((item) => (
              <BalanceHero
                key={`inactive-${item.assignmentId ?? item.absenceType.id}`}
                item={item}
                canManage={false}
                employeeId={employee.id}
                onUnassign={() => {}}
                isUnassigning={false}
                onAdjust={() => {}}
              />
            ))}
          </div>

          {/* Year strip — rolling 2-year window, independent of the ledger
              year selector. Always shows the most recent two years (clamped
              to start at HISTORY_START_YEAR = 2025). */}
          <YearStrip years={stripYears} events={stripEvents} today={today} />

          {/* Ledger */}
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <div className="flex items-baseline justify-between border-b border-border/50 px-4 py-3">
              <div>
                <h3 className="text-[14px] font-semibold tracking-tight">
                  Absence history
                </h3>
                <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                  All accrual + absence events
                  {absenceList.some((a) => a.source === 'peopleforce') &&
                    ', including PeopleForce history'}
                </p>
              </div>
              <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/80 font-mono">
                {ledger.filter((it) => !it.isFuture).length} events
              </span>
            </div>
            {ledger.length === 0 ? (
              <div className="p-8 text-center">
                <History className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-2 text-sm text-muted-foreground">No absences recorded yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {ledger.map((item) => (
                  <LedgerEventRow
                    key={item.key}
                    item={item}
                    onCancel={
                      canCancelImported
                        ? (recordId, label) => setCancelTarget({ recordId, label })
                        : undefined
                    }
                    onEdit={
                      canEditPast || canManageRequests
                        ? (requestId) => setEditRequestId(requestId)
                        : undefined
                    }
                    onDelete={
                      canEditPast || canManageRequests
                        ? (requestId, label) => setDeleteTarget({ requestId, label })
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Absence Type Dialog */}
      <AddAbsenceTypeDialog
        open={addPolicyDialogOpen}
        onOpenChange={setAddPolicyDialogOpen}
        availableAbsenceTypes={availableAbsenceTypes}
        employeeStartDate={employee.startDate}
        onAssign={(absenceTypeId, effectiveFrom) =>
          assignMutation.mutate({ absenceTypeId, effectiveFrom })
        }
        isAssigning={assignMutation.isPending}
      />

      {/* Adjust Balance Dialog */}
      <AdjustBalanceDialog
        open={!!adjustingType}
        onOpenChange={(open) => { if (!open) setAdjustingType(null); }}
        employeeId={employee.id}
        absenceType={adjustingType}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: absenceKeys.employeePolicies(employee.id) });
          // The New/Edit Request dialog reads balances under its own key — refresh
          // it too, or it keeps showing the pre-adjustment balance and may wrongly
          // block Submit until a full page reload.
          queryClient.invalidateQueries({
            queryKey: ['employee-assigned-absence-types', employee.id],
          });
          setAdjustingType(null);
        }}
      />

      {/* Cancel Imported Record confirmation */}
      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open && !cancelImportedMutation.isPending) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel imported absence?</AlertDialogTitle>
            <AlertDialogDescription>
              This voids the PeopleForce record{' '}
              <span className="font-medium text-foreground">{cancelTarget?.label}</span> and returns
              its days to {employee.firstName ?? 'the employee'}&apos;s balance. The record stays in
              history marked as
              cancelled. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelImportedMutation.isPending}>
              Keep record
            </AlertDialogCancel>
            {/* Default AlertDialogAction variant=destructive → soft red-tinted,
                on-brand for the cream theme. No raw bg-red override. */}
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (cancelTarget) cancelImportedMutation.mutate(cancelTarget.recordId);
              }}
              disabled={cancelImportedMutation.isPending}
            >
              {cancelImportedMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Cancel record &amp; restore days
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit absence record — reuses the request dialog in edit mode. */}
      <CreateAbsenceRequestDialog
        open={!!editRequestId}
        onOpenChange={(open) => { if (!open) setEditRequestId(null); }}
        mode="edit"
        editRequestId={editRequestId}
      />

      {/* Delete absence record confirmation. */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteRequestMutation.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete absence record?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this absence record? This will adjust the employee's
              balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRequestMutation.isPending}>
              Keep record
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteRequestMutation.mutate(deleteTarget.requestId);
              }}
              disabled={deleteRequestMutation.isPending}
            >
              {deleteRequestMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete record &amp; adjust balance
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CustomBlocksSection section="ABSENCES" employeeId={employee.id} />
    </div>
  );
}

// Adjust Balance Dialog Component
function AdjustBalanceDialog({
  open,
  onOpenChange,
  employeeId,
  absenceType,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  absenceType: EmployeeAssignedAbsenceType | null;
  onSuccess: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const { user } = useAuthStore();

  const mutation = useMutation({
    mutationFn: () =>
      adjustBalance(employeeId, absenceType!.absenceType.id, {
        amount: parseFloat(amount),
        reason,
        performedById: user?.id,
      }),
    onSuccess: () => {
      setAmount('');
      setReason('');
      onSuccess();
    },
  });

  const parsedAmount = parseFloat(amount);
  const isValid = !isNaN(parsedAmount) && parsedAmount !== 0 && reason.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adjust Balance</DialogTitle>
          <DialogDescription>
            {absenceType && (
              <>
                Manually adjust <span className="font-medium">{absenceType.absenceType.name}</span> balance.
                Current: {absenceType.balance.available?.toFixed(1) ?? 0} / {absenceType.balance.accrued?.toFixed(1) ?? 0} days
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="adjust-amount">Adjustment (days)</Label>
            <Input
              id="adjust-amount"
              type="number"
              step="0.5"
              placeholder="e.g. 2 or -3"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              Positive to add days, negative to subtract
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjust-reason">Reason</Label>
            <Textarea
              id="adjust-reason"
              placeholder="e.g. Migration from PeopleForce, policy correction..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!isValid || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adjusting...
              </>
            ) : (
              'Apply Adjustment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add Absence Type Dialog Component
function AddAbsenceTypeDialog({
  open,
  onOpenChange,
  availableAbsenceTypes,
  employeeStartDate,
  onAssign,
  isAssigning,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableAbsenceTypes: AbsenceType[];
  employeeStartDate: string;
  onAssign: (absenceTypeId: string, effectiveFrom?: string) => void;
  isAssigning: boolean;
}) {
  const [selectedType, setSelectedType] = useState<AbsenceType | null>(null);
  const defaultDate = employeeStartDate?.split('T')[0] || '';
  const [effectiveFrom, setEffectiveFrom] = useState(defaultDate);

  // Reset state when dialog opens/closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedType(null);
      setEffectiveFrom(defaultDate);
    }
    onOpenChange(isOpen);
  };

  const handleBack = () => {
    setSelectedType(null);
  };

  const handleSelectType = (absenceType: AbsenceType) => {
    setSelectedType(absenceType);
    setEffectiveFrom(defaultDate);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        {!selectedType ? (
          <>
            <DialogHeader>
              <DialogTitle>Add Absence Type</DialogTitle>
              <DialogDescription>
                Select an absence type to assign to this employee
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-2">
              {availableAbsenceTypes.length === 0 ? (
                <div className="py-8 text-center">
                  <Check className="mx-auto h-10 w-10 text-emerald-500" />
                  <p className="mt-3 text-sm font-medium">All absence types assigned</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    This employee already has all available absence types
                  </p>
                </div>
              ) : (
                availableAbsenceTypes.map((absenceType) => (
                  <button
                    key={absenceType.id}
                    onClick={() => handleSelectType(absenceType)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                      'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/20',
                      'cursor-pointer'
                    )}
                  >
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${absenceType.color || '#6B7280'}20` }}
                    >
                      <CalendarDays
                        className="h-5 w-5"
                        style={{ color: absenceType.color || '#6B7280' }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{absenceType.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {absenceType.isPaid ? 'Paid' : 'Unpaid'}
                      </p>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Assign {selectedType.name}</DialogTitle>
              <DialogDescription>
                Configure when this policy takes effect
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="effective-from">Effective from</Label>
                <Input
                  id="effective-from"
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Absence policy will accrue from the selected date. By default, this is the
                  employee's hire date, but it can be replaced with another date.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={handleBack} disabled={isAssigning}>
                Back
              </Button>
              <Button
                onClick={() => onAssign(selectedType.id, effectiveFrom || undefined)}
                disabled={isAssigning}
              >
                {isAssigning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  'Assign'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
